import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useWidth } from '../../../hooks/useWidth';
import {
  timeToMinutes,
  getOccupiedIntervals,
  getFilteredStartOptions,
  getFilteredEndOptions,
  validateRange,
} from './workScheduleUtils';
import CircularProgress from '../../common/CircularProgress';

interface WorkSchedule {
  id: string | number;
  psychologistId: number | string;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
  scheduleType?: 'standard' | 'reschedule_only';
}

export type ScheduleAction =
  | { type: 'create'; weekdayIdx: number; startTime: string; endTime: string; scheduleType: 'standard' | 'reschedule_only' }
  | { type: 'update'; scheduleId: string | number; weekdayIdx: number; startTime: string; endTime: string; scheduleType: 'standard' | 'reschedule_only' }
  | { type: 'delete'; scheduleId: string | number };

type PreviewSchedule = {
  id: string | number;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
  scheduleType?: 'standard' | 'reschedule_only';
};

interface WorkSchedulePanelProps {
  existingSchedules: WorkSchedule[];
  onSave: (actions: ScheduleAction[]) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  onHasChangesChange?: (hasChanges: boolean) => void;
  onPreviewChange?: (schedules: PreviewSchedule[]) => void;
  onDeletedSchedulesChange?: (scheduleIds: Set<string | number>) => void;
  mockRescheduleEntry?: boolean;
  /** Default schedule type for newly added ranges. 'standard' (default) or 'reschedule_only'. */
  defaultScheduleType?: 'standard' | 'reschedule_only';
  /** Called when the user clicks the "Cómo funciona tu agenda" help link. */
  onOpenAgendaHelp?: () => void;
}

type TimeRange = { id?: string | number; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' };
type DayState = { enabled: boolean; ranges: TimeRange[] };
type WeekSchedule = Record<number, DayState>;

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function buildWeekSchedule(schedules: WorkSchedule[]): WeekSchedule {
  const week: WeekSchedule = {};
  const hasAnySchedule = schedules.some(s => s.active);
  for (let i = 0; i < 7; i++) {
    week[i] = hasAnySchedule
      ? { enabled: false, ranges: [] }
      : { enabled: true, ranges: [{ startTime: '', endTime: '' }] };
  }
  for (const s of schedules) {
    if (!s.active) continue;
    const day = week[s.weekday];
    if (!day) continue;
    day.enabled = true;
    day.ranges.push({ id: s.id, startTime: s.startTime, endTime: s.endTime, scheduleType: s.scheduleType });
  }
  for (let i = 0; i < 7; i++) {
    week[i].ranges.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
  }
  return week;
}

function computeScheduleDiff(
  initial: WeekSchedule,
  current: WeekSchedule,
): ScheduleAction[] {
  const actions: ScheduleAction[] = [];

  for (let day = 0; day < 7; day++) {
    const initDay = initial[day];
    const currDay = current[day];

    const initById = new Map<string, TimeRange>();
    for (const r of initDay.ranges) {
      if (r.id != null) initById.set(String(r.id), r);
    }

    const currById = new Map<string, TimeRange>();
    for (const r of currDay.ranges) {
      if (r.id != null) currById.set(String(r.id), r);
    }

    if (!currDay.enabled && initDay.enabled) {
      for (const r of initDay.ranges) {
        if (r.id != null) {
          actions.push({ type: 'delete', scheduleId: r.id });
        }
      }
      continue;
    }

    if (!currDay.enabled) continue;

    Array.from(initById.keys()).forEach(id => {
      if (!currById.has(id)) {
        const initRange = initById.get(id);
        if (initRange && initRange.id != null) {
          actions.push({ type: 'delete', scheduleId: initRange.id });
        }
      }
    });

    for (const r of currDay.ranges) {
      if (r.id != null && initById.has(String(r.id))) {
        const orig = initById.get(String(r.id));
        if (!orig) continue;
        if (orig.startTime !== r.startTime || orig.endTime !== r.endTime || orig.scheduleType !== r.scheduleType) {
          actions.push({
            type: 'update',
            scheduleId: r.id,
            weekdayIdx: day,
            startTime: r.startTime,
            endTime: r.endTime,
            scheduleType: r.scheduleType ?? 'standard',
          });
        }
      } else {
        if (!r.startTime && !r.endTime) continue;
        actions.push({
          type: 'create',
          weekdayIdx: day,
          startTime: r.startTime,
          endTime: r.endTime,
          scheduleType: r.scheduleType ?? 'standard',
        });
      }
    }
  }

  return actions;
}

// Find the closest option to a partial typed string (e.g. "2" -> "02:00", "2:3" -> "02:30", "14" -> "14:00")
function findClosestOption(typed: string, options: string[]): string | null {
  if (!typed) return null;
  // If input contains a colon, split into hour:minute parts
  if (typed.includes(':')) {
    const [hPart, mPart = ''] = typed.split(':');
    const h = parseInt(hPart, 10);
    if (isNaN(h)) return null;
    const hour = Math.min(23, Math.max(0, h));
    // Pad partial minutes: "3" -> 30, "30" -> 30, "" -> 0
    const mDigits = mPart.replace(/\D/g, '');
    const min = mDigits ? Math.min(59, parseInt(mDigits.length === 1 ? mDigits + '0' : mDigits, 10)) : 0;
    const target = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    const match = options.find(o => o >= target);
    return match || options[options.length - 1] || null;
  }
  const num = parseInt(typed, 10);
  if (isNaN(num)) return null;
  // 1-2 digits: treat as hour (e.g. "2" -> "02:00", "14" -> "14:00")
  if (typed.length <= 2) {
    const hour = Math.min(23, Math.max(0, num));
    const target = `${String(hour).padStart(2, '0')}:00`;
    const match = options.find(o => o >= target);
    return match || options[options.length - 1] || null;
  }
  // 3-4 digits: treat as HHMM (e.g. "130" -> "01:30", "1430" -> "14:30")
  const h = Math.floor(num / 100);
  const m = num % 100;
  const target = `${String(Math.min(23, h)).padStart(2, '0')}:${String(Math.min(59, m)).padStart(2, '0')}`;
  const match = options.find(o => o >= target);
  return match || options[options.length - 1] || null;
}

// Dropdown needs fixed positioning via portal — style required for dynamic coords
const PanelTimeSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  ariaLabel?: string;
}> = ({ value, onChange, options, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number; maxH: number }>({ top: 0, left: 0, width: 0, maxH: 220 });
  const [highlightedOption, setHighlightedOption] = useState<string | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setIsEditing(false);
        setInputValue('');
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setIsEditing(false);
        setInputValue('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);

    const updatePos = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const maxH = Math.max(140, Math.min(260, spaceBelow));
      setMenuPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 80), maxH });
    };
    updatePos();
    if (open) {
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true);
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  // Scroll to highlighted option
  useEffect(() => {
    if (!highlightedOption || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-value="${highlightedOption}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedOption]);

  // Scroll to current value when opening
  useEffect(() => {
    if (!open || !listRef.current || !value) return;
    const target = highlightedOption || value;
    const el = listRef.current.querySelector(`[data-value="${target}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputChange = (typed: string) => {
    // Only allow digits and colon
    const clean = typed.replace(/[^\d:]/g, '');
    setInputValue(clean);
    const closest = findClosestOption(clean, options);
    setHighlightedOption(closest);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (highlightedOption) {
        onChange(highlightedOption);
      }
      setOpen(false);
      setIsEditing(false);
      setInputValue('');
      e.preventDefault();
    }
  };

  const handleClick = () => {
    if (!open) {
      setOpen(true);
      setIsEditing(true);
      setInputValue('');
      setHighlightedOption(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        aria-label={ariaLabel}
        value={isEditing ? inputValue : (value || '--:--')}
        placeholder={value || '--:--'}
        onClick={handleClick}
        onFocus={() => {
          if (!open) {
            setOpen(true);
            setIsEditing(true);
            setInputValue('');
            setHighlightedOption(null);
          }
        }}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleInputKeyDown}
        readOnly={!isEditing}
        className="w-full py-1.5 px-2 text-[0.8125rem] border border-gray-300 rounded-md bg-white text-left cursor-pointer focus:border-violet-700 focus:outline-none hover:border-gray-400"
      />
      {open && createPortal(
        <div
          ref={listRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: `${menuPos.top}px`,
            left: `${menuPos.left}px`,
            width: `${menuPos.width}px`,
            maxHeight: menuPos.maxH,
          }}
          className="bg-white border border-gray-200 rounded-md shadow-lg overflow-y-auto z-[3000]"
        >
          {options.map(t => (
            <div
              key={t}
              data-value={t}
              role="option"
              aria-selected={t === value}
              onMouseDown={(e) => { e.preventDefault(); onChange(t); setOpen(false); setIsEditing(false); setInputValue(''); }}
              className={`py-1.5 px-2 cursor-pointer text-[0.8125rem] hover:bg-gray-50 ${
                t === highlightedOption ? 'bg-violet-100 font-semibold' : t === value ? 'bg-violet-50' : 'bg-white'
              }`}
            >
              {t}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

/**
 * Tooltip de tipo de horario renderizado vía portal en document.body.
 * Usa position:fixed para escapar de overflow-hidden/overflow-y-auto del panel.
 */
const PanelInfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const show = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // Posicionar a la izquierda del ícono, centrado verticalmente
    setPos({ top: r.top + r.height / 2, left: r.left - 8 });
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Información sobre tipo de horario"
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onFocus={show}
        onBlur={() => setOpen(false)}
        className="flex items-center justify-center border-none bg-transparent cursor-pointer rounded-full hover:bg-[#f3f0ff] transition-colors"
        style={{ width: 18, height: 18, padding: 0, flexShrink: 0 }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8475b3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </button>
      {open && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: 'translate(-100%, -50%)',
            marginLeft: -6,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem',
            color: '#6b7280',
            fontSize: '0.8125rem',
            lineHeight: 1.4,
            width: 240,
            maxWidth: 'calc(100vw - 24px)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'normal',
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
};

const TimeRangeRow: React.FC<{
  range: TimeRange;
  index: number;
  allRanges: TimeRange[];
  canRemove: boolean;
  error: string;
  onChange: (index: number, field: 'startTime' | 'endTime', value: string) => void;
  onRemove: (index: number) => void;
  schedulesForDay?: Array<{ startTime: string; endTime: string }>;
  onScheduleTypeChange?: (index: number, value: 'standard' | 'reschedule_only') => void;
}> = ({ range, index, allRanges, canRemove, error, onChange, onRemove, schedulesForDay = [], onScheduleTypeChange }) => {
  const occupied = useMemo(
    () => getOccupiedIntervals(index, allRanges, schedulesForDay),
    [index, allRanges, schedulesForDay],
  );
  const startOpts = useMemo(() => getFilteredStartOptions(occupied, range.endTime), [occupied, range.endTime]);
  const endOpts = useMemo(
    () => getFilteredEndOptions(range.startTime, occupied),
    [range.startTime, occupied],
  );

  return (
    <div className="mb-1">
      <div className="flex items-center gap-1.5">
        <div className="flex-1 min-w-0">
          <PanelTimeSelect
            value={range.startTime}
            onChange={(v) => onChange(index, 'startTime', v)}
            options={startOpts}
            ariaLabel={`Hora inicio rango ${index + 1}`}
          />
        </div>
        <span className="text-gray-400 text-xs shrink-0">—</span>
        <div className="flex-1 min-w-0">
          <PanelTimeSelect
            value={range.endTime}
            onChange={(v) => onChange(index, 'endTime', v)}
            options={endOpts}
            ariaLabel={`Hora fin rango ${index + 1}`}
          />
        </div>
        {onScheduleTypeChange && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => onScheduleTypeChange(index, range.scheduleType === 'reschedule_only' ? 'standard' : 'reschedule_only')}
              className={`shrink-0 px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold border transition-colors ${
                range.scheduleType === 'reschedule_only'
                  ? 'bg-[#ece8f5] text-[#4a3d73] border-[#4a3d73] hover:bg-[#ddd7f0] cursor-pointer'
                  : 'bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-100 cursor-pointer'
              }`}
            >
              {range.scheduleType === 'reschedule_only' ? 'Repr.' : 'Est.'}
            </button>
            <PanelInfoTooltip
              text={
                range.scheduleType === 'reschedule_only'
                  ? 'Reprogramación: exclusivo para reagendar con pacientes activos.'
                  : 'Estándar: disponible para pacientes nuevos y activos.'
              }
            />
          </div>
        )}
        <button
          type="button"
          disabled={!canRemove}
          onClick={() => canRemove && onRemove(index)}
          className={`flex items-center justify-center w-6 h-6 bg-transparent border-none rounded text-sm shrink-0 transition-colors ${
            canRemove
              ? 'cursor-pointer text-red-500 hover:bg-red-50'
              : 'cursor-not-allowed text-gray-300'
          }`}
          aria-label="Eliminar rango"
        >
          ✕
        </button>
      </div>
      {error && (
        <div className="py-1 px-2 bg-red-50 border border-red-200 rounded text-red-600 text-[0.6875rem] mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

const DayRow: React.FC<{
  dayIndex: number;
  day: DayState;
  isOnlyEnabledDay: boolean;
  onToggle: (dayIndex: number) => void;
  onRangeChange: (dayIndex: number, rangeIndex: number, field: 'startTime' | 'endTime', value: string) => void;
  onRemoveRange: (dayIndex: number, rangeIndex: number) => void;
  onAddRange: (dayIndex: number) => void;
  onCopyToAll: (sourceDayIndex: number) => void;
  errors: string[];
  onScheduleTypeChange?: (dayIndex: number, rangeIndex: number, value: 'standard' | 'reschedule_only') => void;
}> = ({ dayIndex, day, isOnlyEnabledDay, onToggle, onRangeChange, onRemoveRange, onAddRange, onCopyToAll, errors, onScheduleTypeChange }) => {
  return (
    <div className="py-2.5 px-4 border-b border-gray-100">
      <div className={`flex items-center gap-2 ${day.enabled && day.ranges.length > 0 ? 'mb-2' : ''}`}>
        <input
          type="checkbox"
          checked={day.enabled}
          onChange={() => onToggle(dayIndex)}
          className="w-4 h-4 cursor-pointer accent-violet-700"
        />
        <span className={`text-[0.8125rem] font-semibold flex-1 ${day.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
          {DAY_NAMES[dayIndex]}
        </span>
        {day.enabled && day.ranges.length > 0 && (
          <button
            type="button"
            onClick={() => onCopyToAll(dayIndex)}
            title="Copiar horario en todos los días"
            className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded cursor-pointer text-violet-500 transition-colors hover:text-violet-700 hover:bg-violet-50"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5.5" y="1.5" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10.5 13V13.5C10.5 14.3284 9.82843 15 9 15H3.5C2.67157 15 2 14.3284 2 13.5V5.5C2 4.67157 2.67157 4 3.5 4H4" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        )}
        {!day.enabled && (
          <span className="text-[0.6875rem] text-gray-400">No disponible</span>
        )}
      </div>
      {day.enabled && (
        <div className="pl-6">
          {day.ranges.map((range, idx) => (
            <TimeRangeRow
              key={range.id != null ? String(range.id) : `new-${idx}`}
              range={range}
              index={idx}
              allRanges={day.ranges}
              canRemove={!(isOnlyEnabledDay && day.ranges.length <= 1)}
              error={errors[idx] || ''}
              onChange={(i, field, value) => onRangeChange(dayIndex, i, field, value)}
              onRemove={(i) => onRemoveRange(dayIndex, i)}
              onScheduleTypeChange={onScheduleTypeChange ? (i, v) => onScheduleTypeChange(dayIndex, i, v) : undefined}
            />
          ))}
          <button
            type="button"
            onClick={() => onAddRange(dayIndex)}
            title="Agregar otro período a este día"
            className="inline-flex items-center gap-1 py-1 px-2 text-xs font-semibold text-violet-700 bg-transparent border-none rounded cursor-pointer transition-colors mt-0.5 hover:bg-violet-50"
          >
            + Agregar horario
          </button>
        </div>
      )}
    </div>
  );
};

const WorkSchedulePanel: React.FC<WorkSchedulePanelProps> = ({ existingSchedules, onSave, onCancel, loading = false, onHasChangesChange, onPreviewChange, onDeletedSchedulesChange, mockRescheduleEntry = false, defaultScheduleType = 'standard', onOpenAgendaHelp }) => {
  const { size } = useWidth();
  const isMobile = size === 's';
  const [vpWidth, setVpWidth] = useState(() => window.visualViewport?.width ?? window.innerWidth);
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVpWidth(vv.width);
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [isMobile]);
  const schedulesFingerprint = useMemo(
    () => existingSchedules.map(s => `${s.id}:${s.weekday}:${s.startTime}:${s.endTime}`).sort().join('|'),
    [existingSchedules],
  );

  const [weekSchedule, setWeekSchedule] = useState<WeekSchedule>(() => buildWeekSchedule(existingSchedules));
  const initialRef = useRef<WeekSchedule>(buildWeekSchedule(existingSchedules));
  const [saving, setSaving] = useState(false);
  const [copyConfirmDayIndex, setCopyConfirmDayIndex] = useState<number | null>(null);
  const stashedRangesRef = useRef<Record<number, TimeRange[]>>({});
  // Fila de cada día + contenedor scrolleable del panel — para llevar la fila a la vista
  // cuando el usuario habilita o edita el horario de ese día. Calculamos el scrollTop a
  // mano (en vez de scrollIntoView) para no depender de cómo el browser resuelva el
  // ancestro scrolleable, que puede no ser el que esperamos.
  const dayRowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  const scrollDayIntoView = useCallback((dayIndex: number) => {
    // Se ejecuta en el próximo frame: si veníamos de habilitar el día, esperamos a que el
    // re-render agregue sus inputs (cambia la altura de la fila) antes de medir posiciones.
    requestAnimationFrame(() => {
      const container = scrollBodyRef.current;
      const row = dayRowRefs.current[dayIndex];
      if (!container || !row) return;
      const containerRect = container.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      if (rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom) return;
      const delta = rowRect.top - containerRect.top;
      container.scrollTop = Math.max(0, container.scrollTop + delta - 8);
    });
  }, []);

  useEffect(() => {
    const fresh = buildWeekSchedule(existingSchedules);
    setWeekSchedule(fresh);
    initialRef.current = fresh;
    stashedRangesRef.current = {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulesFingerprint]);

  const toggleDay = useCallback((dayIndex: number) => {
    setWeekSchedule(prev => {
      const day = prev[dayIndex];
      if (day.enabled) {
        const enabledCount = Object.values(prev).filter(d => d.enabled).length;
        if (enabledCount <= 1) return prev;
        stashedRangesRef.current[dayIndex] = day.ranges;
        return { ...prev, [dayIndex]: { enabled: false, ranges: [] } };
      } else {
        const stashed = stashedRangesRef.current[dayIndex];
        const initial = initialRef.current[dayIndex];
        const ranges = (stashed && stashed.length > 0)
          ? stashed
          : (initial && initial.ranges.length > 0)
            ? initial.ranges
            : [{ startTime: '', endTime: '' }];
        return { ...prev, [dayIndex]: { enabled: true, ranges } };
      }
    });
  }, []);

  const updateRange = useCallback((dayIndex: number, rangeIndex: number, field: 'startTime' | 'endTime', value: string) => {
    setWeekSchedule(prev => {
      const day = { ...prev[dayIndex] };
      const ranges = [...day.ranges];
      ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: value };
      const allComplete = ranges.every(r => r.startTime && r.endTime);
      if (allComplete) {
        ranges.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
      }
      day.ranges = ranges;
      return { ...prev, [dayIndex]: day };
    });
  }, []);

  const removeRange = useCallback((dayIndex: number, rangeIndex: number) => {
    setWeekSchedule(prev => {
      const day = { ...prev[dayIndex] };
      const enabledCount = Object.values(prev).filter(d => d.enabled).length;
      if (enabledCount <= 1 && day.enabled && day.ranges.length <= 1) return prev;
      day.ranges = day.ranges.filter((_, i) => i !== rangeIndex);
      if (day.ranges.length === 0) {
        // En vez de deshabilitar el día, dejamos un input vacío listo para completar.
        day.ranges = [{ startTime: '', endTime: '' }];
      }
      return { ...prev, [dayIndex]: day };
    });
  }, []);

  const nextTempIdRef = useRef(0);
  // Kept in a ref so addRange/addRangeWithTimes don't need it in their dep array.
  const defaultScheduleTypeRef = useRef(defaultScheduleType);
  useEffect(() => { defaultScheduleTypeRef.current = defaultScheduleType; }, [defaultScheduleType]);

  const addRange = useCallback((dayIndex: number) => {
    setWeekSchedule(prev => {
      const day = { ...prev[dayIndex] };
      day.ranges = [...day.ranges, { id: `new-${nextTempIdRef.current++}`, startTime: '', endTime: '', scheduleType: defaultScheduleTypeRef.current }];
      return { ...prev, [dayIndex]: day };
    });
  }, []);

  const updateRangeScheduleType = useCallback((dayIndex: number, rangeIndex: number, value: 'standard' | 'reschedule_only') => {
    setWeekSchedule(prev => {
      const day = { ...prev[dayIndex] };
      const ranges = [...day.ranges];
      ranges[rangeIndex] = { ...ranges[rangeIndex], scheduleType: value };
      day.ranges = ranges;
      return { ...prev, [dayIndex]: day };
    });
  }, []);

  const addRangeWithTimes = useCallback((dayIndex: number, startTime: string, endTime: string) => {
    setWeekSchedule(prev => {
      const day = { ...prev[dayIndex] };
      // Enable the day if it's not already enabled
      if (!day.enabled) {
        day.enabled = true;
      }
      // Al agregar un horario desde la agenda (drag), si había un input vacío esperando
      // completarse, lo reemplaza en vez de sumar uno al lado (a diferencia de "+ Agregar
      // horario", que siempre suma uno nuevo).
      const emptyIdx = day.ranges.findIndex(r => !r.startTime && !r.endTime);
      if (emptyIdx !== -1) {
        const ranges = [...day.ranges];
        ranges[emptyIdx] = { ...ranges[emptyIdx], startTime, endTime };
        day.ranges = ranges;
      } else {
        day.ranges = [...day.ranges, { id: `new-${nextTempIdRef.current++}`, startTime, endTime, scheduleType: defaultScheduleTypeRef.current }];
      }
      day.ranges.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
      return { ...prev, [dayIndex]: day };
    });
  }, []);

  const updateRangeById = useCallback((scheduleId: string | number, weekdayIdx: number, newStartTime: string, newEndTime: string, newScheduleType?: 'standard' | 'reschedule_only') => {
    setWeekSchedule(prev => {
      const day = { ...prev[weekdayIdx] };
      const rangeIndex = day.ranges.findIndex(r => r.id === scheduleId);
      if (rangeIndex === -1) return prev;

      const updatedRanges = [...day.ranges];
      updatedRanges[rangeIndex] = { ...updatedRanges[rangeIndex], startTime: newStartTime, endTime: newEndTime, ...(newScheduleType ? { scheduleType: newScheduleType } : {}) };
      day.ranges = updatedRanges;
      return { ...prev, [weekdayIdx]: day };
    });
  }, []);

  const deleteRangeById = useCallback((scheduleId: string | number) => {
    setWeekSchedule(prev => {
      const next = { ...prev };
      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const day = { ...next[dayIdx] };
        const rangeIndex = day.ranges.findIndex(r => r.id === scheduleId);
        if (rangeIndex !== -1) {
          day.ranges = day.ranges.filter((_, i) => i !== rangeIndex);
          if (day.ranges.length === 0) {
            day.enabled = false;
          }
          next[dayIdx] = day;
          break;
        }
      }
      return next;
    });
  }, []);

  const restoreScheduleById = useCallback((scheduleId: string | number) => {
    // Find the schedule in the initial state
    const initialSchedule = existingSchedules.find(s => s.id === scheduleId);
    if (!initialSchedule) return;

    setWeekSchedule(prev => {
      const day = { ...prev[initialSchedule.weekday] };
      // Check if the range already exists (it shouldn't, but just in case)
      const exists = day.ranges.some(r => r.id === scheduleId);
      if (exists) return prev;

      // Enable the day if it's not already enabled
      if (!day.enabled) {
        day.enabled = true;
      }

      // Add the range back
      day.ranges = [...day.ranges, {
        id: initialSchedule.id,
        startTime: initialSchedule.startTime,
        endTime: initialSchedule.endTime,
        scheduleType: initialSchedule.scheduleType,
      }];

      // Sort ranges by start time
      day.ranges.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

      return { ...prev, [initialSchedule.weekday]: day };
    });
  }, [existingSchedules]);

  const handleCopyClick = useCallback((sourceDayIndex: number) => {
    setCopyConfirmDayIndex(sourceDayIndex);
  }, []);

  const handleCopyConfirm = useCallback(() => {
    if (copyConfirmDayIndex === null) return;
    setWeekSchedule(prev => {
      const sourceRanges = prev[copyConfirmDayIndex].ranges;
      const next = { ...prev };
      for (let i = 0; i < 7; i++) {
        if (i === copyConfirmDayIndex) continue;
        next[i] = {
          enabled: true,
          ranges: sourceRanges.map(r => ({ startTime: r.startTime, endTime: r.endTime, scheduleType: r.scheduleType })),
        };
      }
      return next;
    });
    setCopyConfirmDayIndex(null);
  }, [copyConfirmDayIndex]);

  const handleCopyCancel = useCallback(() => {
    setCopyConfirmDayIndex(null);
  }, []);

  const allErrors = useMemo(() => {
    const result: Record<number, string[]> = {};
    for (let day = 0; day < 7; day++) {
      const d = weekSchedule[day];
      if (!d.enabled) {
        result[day] = [];
        continue;
      }
      result[day] = d.ranges.map((r, i) => validateRange(r, i, d.ranges, []));
    }
    return result;
  }, [weekSchedule]);

  const hasErrors = useMemo(() => {
    for (let day = 0; day < 7; day++) {
      if (allErrors[day]?.some(e => e !== '')) return true;
    }
    return false;
  }, [allErrors]);

  const hasIncompleteRanges = useMemo(() => {
    for (let day = 0; day < 7; day++) {
      const d = weekSchedule[day];
      if (!d.enabled) continue;
      if (d.ranges.some(r => {
        if (!r.startTime && !r.endTime) return false;
        return !r.startTime || !r.endTime;
      })) return true;
    }
    return false;
  }, [weekSchedule]);

  const actions = useMemo(
    () => computeScheduleDiff(initialRef.current, weekSchedule),
    [weekSchedule],
  );

  const deletedScheduleIds = useMemo(() => {
    const ids = new Set<string | number>();
    actions.forEach(action => {
      if (action.type === 'delete') {
        ids.add(action.scheduleId);
      }
    });
    return ids;
  }, [actions]);

  const hasChanges = actions.length > 0;
  const canSave = hasChanges && !hasErrors && !hasIncompleteRanges && !saving && !loading;

  // Notify parent about changes state
  useEffect(() => {
    onHasChangesChange?.(hasChanges);
  }, [hasChanges, onHasChangesChange]);

  // Notify parent about deleted schedules
  useEffect(() => {
    onDeletedSchedulesChange?.(deletedScheduleIds);
  }, [deletedScheduleIds, onDeletedSchedulesChange]);

  // Handle add range from drag (from calendar drag selection)
  useEffect(() => {
    const handler = (e: Event) => {
      const { weekdayIdx, startTime, endTime } = (e as CustomEvent<{ weekdayIdx: number; startTime: string; endTime: string }>).detail;
      addRangeWithTimes(weekdayIdx, startTime, endTime);
    };
    window.addEventListener('psi-add-work-range-from-drag', handler);
    return () => window.removeEventListener('psi-add-work-range-from-drag', handler);
  }, [addRangeWithTimes]);

  // Handle update range from edit modal
  useEffect(() => {
    const handler = (e: Event) => {
      const { scheduleId, weekdayIdx, startTime, endTime, scheduleType } = (e as CustomEvent<{ scheduleId: string | number; weekdayIdx: number; startTime: string; endTime: string; scheduleType?: 'standard' | 'reschedule_only' }>).detail;
      updateRangeById(scheduleId, weekdayIdx, startTime, endTime, scheduleType);
    };
    window.addEventListener('psi-update-work-range-from-edit', handler);
    return () => window.removeEventListener('psi-update-work-range-from-edit', handler);
  }, [updateRangeById]);

  // Handle delete range from edit modal
  useEffect(() => {
    const handler = (e: Event) => {
      const { scheduleId } = (e as CustomEvent<{ scheduleId: string | number }>).detail;
      deleteRangeById(scheduleId);
    };
    window.addEventListener('psi-delete-work-range-from-edit', handler);
    return () => window.removeEventListener('psi-delete-work-range-from-edit', handler);
  }, [deleteRangeById]);

  // Handle restore deleted schedule (undo deletion)
  useEffect(() => {
    const handler = (e: Event) => {
      const { scheduleId } = (e as CustomEvent<{ scheduleId: string | number }>).detail;
      restoreScheduleById(scheduleId);
    };
    window.addEventListener('psi-restore-work-schedule', handler);
    return () => window.removeEventListener('psi-restore-work-schedule', handler);
  }, [restoreScheduleById]);

  // Emit live preview of current schedule state to parent
  useEffect(() => {
    if (!onPreviewChange) return;
    const flat: PreviewSchedule[] = [];
    let tempId = 0;
    for (let day = 0; day < 7; day++) {
      const d = weekSchedule[day];
      if (!d.enabled) continue;
      for (const r of d.ranges) {
        if (!r.startTime || !r.endTime) continue;
        flat.push({
          id: r.id ?? `preview-${tempId++}`,
          weekday: day,
          startTime: r.startTime,
          endTime: r.endTime,
          active: true,
          scheduleType: r.scheduleType ?? 'standard',
        });
      }
    }
    onPreviewChange(flat);
  }, [weekSchedule, onPreviewChange]);

  // Listen for external save trigger (from CalendarTab unsaved modal)
  useEffect(() => {
    const handler = async () => {
      if (canSave) {
        setSaving(true);
        try {
          await onSave(actions);
          window.dispatchEvent(new CustomEvent('psi-schedule-saved'));
        } catch {
          window.dispatchEvent(new CustomEvent('psi-schedule-save-failed'));
        } finally {
          setSaving(false);
        }
      } else if (hasChanges) {
        // Has changes but can't save (errors)
        window.dispatchEvent(new CustomEvent('psi-schedule-save-failed'));
      }
    };
    window.addEventListener('psi-trigger-schedule-save', handler);
    return () => window.removeEventListener('psi-trigger-schedule-save', handler);
  }, [canSave, hasChanges, actions, onSave]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(actions);
      onCancel?.();
    } finally {
      setSaving(false);
    }
  };

  const handleUndo = useCallback(() => {
    setWeekSchedule(initialRef.current);
    stashedRangesRef.current = {};
  }, []);

  // Reset externo (usado por onboarding después del mock save)
  useEffect(() => {
    const handler = () => {
      setWeekSchedule(initialRef.current);
      stashedRangesRef.current = {};
    };
    window.addEventListener('psi-work-schedule-reset', handler);
    return () => window.removeEventListener('psi-work-schedule-reset', handler);
  }, []);

  const panelNode = (
    <div id="work-schedule-panel"
      className={
        isMobile
          ? 'fixed bottom-0 left-0 max-h-[85vh] rounded-t-2xl z-[1300] bg-white border border-gray-200 shadow-lg flex flex-col overflow-hidden'
          : 'w-[33%] min-w-[280px] max-w-96 max-h-[37.5rem] bg-white border shadow-lg border-gray-200 rounded-xl flex flex-col overflow-hidden relative z-10'
      }
      style={isMobile ? { width: vpWidth } : undefined}
    >
      {/* Mobile drag handle */}
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
      )}
      {/* Header */}
      <div className="py-3.5 px-4 border-b border-gray-200 bg-violet-50 flex items-center justify-between">
        <h3 className="m-0 text-[0.9375rem] font-bold text-violet-700">
          Horario de trabajo
        </h3>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded cursor-pointer text-gray-500 text-base transition-colors hover:bg-gray-100"
            aria-label="Cerrar"
          >
            ×
          </button>
        )}
      </div>

      {/* Link de ayuda: "Cómo funciona tu agenda" — común a los 3 modales de configuración */}
      {onOpenAgendaHelp && (
        <div
          data-testid="agenda-help-cta-banner"
          className="shrink-0 px-4 py-2.5 bg-violet-50 border-b border-violet-100 flex items-center"
        >
          <button
            type="button"
            onClick={onOpenAgendaHelp}
            className="text-[0.75rem] text-[#6D57BC] font-medium bg-transparent border-none cursor-pointer p-0 text-left hover:underline underline-offset-2 transition-all"
          >
            ¿Querés entender cómo funciona tu agenda? →
          </button>
        </div>
      )}

      {/* Scrollable body */}
      <div ref={scrollBodyRef} className="flex-1 overflow-y-auto overflow-x-hidden relative" style={{ scrollbarWidth: 'thin', scrollbarColor: '#6D57BC #E5E7EB', scrollbarGutter: 'stable', height: '50%' }}>
        {(saving || loading) && (
          <div className="absolute inset-0 bg-white/80 z-10 grid place-items-center">
            <CircularProgress sizeRem={2} label="Guardando..." />
          </div>
        )}
        {
          Array.from({ length: 7 }, (_, i) => {
            const enabledCount = Object.values(weekSchedule).filter(d => d.enabled).length;
            return (
            <div key={i} ref={(el) => { dayRowRefs.current[i] = el; }}>
              <DayRow
                dayIndex={i}
                day={weekSchedule[i]}
                isOnlyEnabledDay={enabledCount <= 1 && weekSchedule[i].enabled}
                onToggle={(idx) => {
                  if (!weekSchedule[idx].enabled) scrollDayIntoView(idx);
                  toggleDay(idx);
                }}
                onRangeChange={(idx, rangeIdx, field, value) => {
                  scrollDayIntoView(idx);
                  updateRange(idx, rangeIdx, field, value);
                }}
                onRemoveRange={removeRange}
                onAddRange={addRange}
                onCopyToAll={handleCopyClick}
                errors={allErrors[i] || []}
                onScheduleTypeChange={updateRangeScheduleType}
              />
            </div>
          );})
        }
        {/* Mock entry for onboarding tour — looks like a real DayRow */}
        {mockRescheduleEntry && (
          <div id="onboarding-panel-day-row" className="py-2.5 px-4 border-b border-[#c4b7e8] bg-[#f5f2fc]/50">
            <div id="onboarding-panel-day-header" className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked readOnly className="w-4 h-4 accent-violet-700 pointer-events-none" tabIndex={-1} />
              <span className="text-[0.8125rem] font-semibold text-gray-900 flex-1">Ejemplo</span>
              <button type="button" tabIndex={-1} className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded text-violet-500 pointer-events-none">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5.5" y="1.5" width="8" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10.5 13V13.5C10.5 14.3284 9.82843 15 9 15H3.5C2.67157 15 2 14.3284 2 13.5V5.5C2 4.67157 2.67157 4 3.5 4H4" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
            </div>
            <div className="pl-6">
              <div className="mb-1">
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="w-full py-1 px-2 bg-gray-50 border border-gray-200 rounded text-[0.75rem] text-gray-500">09:00</div>
                  </div>
                  <span className="text-gray-400 text-xs shrink-0">—</span>
                  <div className="flex-1 min-w-0">
                    <div className="w-full py-1 px-2 bg-gray-50 border border-gray-200 rounded text-[0.75rem] text-gray-500">12:00</div>
                  </div>
                  <span id="onboarding-reschedule-mock-row" className="shrink-0 px-1.5 py-0.5 rounded-full text-[0.6rem] font-semibold border bg-[#ece8f5] text-[#4a3d73] border-[#4a3d73]">
                    Repr.
                  </span>
                  <button type="button" tabIndex={-1} className="flex items-center justify-center w-6 h-6 bg-transparent border-none rounded text-sm shrink-0 text-red-500 pointer-events-none" aria-label="Eliminar rango">
                    ✕
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!saving && !loading && (
        <div className="py-3 px-4 border-t border-gray-200 bg-gray-50 shadow-lg">
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            style={canSave ? { background: '#6D57BC' } : { background: '#9ca3af', opacity: 0.6 }}
            className={`w-full py-2.5 px-4 text-sm font-semibold text-white border-none rounded-lg transition-colors ${
              canSave
                ? 'cursor-pointer hover:brightness-90'
                : 'cursor-not-allowed'
            }`}
          >
            Guardar cambios
          </button>
          {hasChanges && (
            <button
              type="button"
              onClick={handleUndo}
              className="w-full mt-1.5 py-2 px-4 text-sm font-semibold text-gray-500 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Deshacer
            </button>
          )}
          {onCancel && !hasChanges && (
            <button
              type="button"
              onClick={() => onCancel()}
              className="w-full mt-1.5 py-2 px-4 text-sm font-semibold text-gray-500 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              Cancelar
            </button>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {copyConfirmDayIndex !== null && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Confirmar copia de horario
            </h3>
            <p className="text-sm text-gray-700 mb-6">
              ¿Está seguro de copiar la configuración del <strong>{DAY_NAMES[copyConfirmDayIndex]}</strong> a todos los días?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCopyCancel}
                className="py-2 px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCopyConfirm}
                className="py-2 px-4 text-sm font-semibold text-white bg-violet-700 border-none rounded-lg cursor-pointer transition-colors hover:bg-violet-800"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <>
        {onCancel && <div className="fixed inset-0 bg-black/40 z-[1299]" onClick={onCancel} />}
        {panelNode}
      </>,
      document.body,
    );
  }

  return panelNode;
};

export default WorkSchedulePanel;
export { computeScheduleDiff, PanelTimeSelect, TimeRangeRow };
export type { TimeRange, PreviewSchedule };
